import * as React from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { usePdfFromHtml } from "@/hooks/usePdfFromHtml";
import { BoletoBarcode } from "./BoletoBarcode";

// Reusing the same type definitions
interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface Party {
  name: string;
  document: string;
  email: string;
  phone: string;
  address: Address;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Totals {
  subtotal: number;
  tax: number;
  total: number;
}

interface PaymentInfo {
  method: string;
  dueDate: string;
  instructions: string;
}

interface InvoiceMeta {
  number: string;
  issueDate: string;
  dueDate: string;
  currency: string;
}

interface InvoiceData {
  meta: InvoiceMeta;
  issuer: Party;
  customer: Party;
  items: InvoiceItem[];
  totals: Totals;
  paymentInfo: PaymentInfo;
  notes: string;
  pixQrCodeBase64?: string;
  boletoUrl?: string;
  payment?: {
    pixPayload?: string;
    pixKey?: string;
    boletoLine?: string;
    boletoBarcode?: string;
  };
}

interface InvoiceProps {
  data?: Partial<InvoiceData>;
  logoUrl?: string;
}

// Utility functions (reused)
const fmtBRL = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const fmtDate = (dateStr: string): string => {
  if (!dateStr) return '';
  // Handle both date objects and strings
  const date = typeof dateStr === 'string' && dateStr.includes('/') 
    ? dateStr 
    : new Date(dateStr).toLocaleDateString('pt-BR');
  return date;
};

const ensureAddress = (addr?: Partial<Address>): Address => ({
  street: addr?.street || '',
  city: addr?.city || '',
  state: addr?.state || '',
  zipCode: addr?.zipCode || '',
  country: addr?.country || 'Brasil'
});

const ensureParty = (party?: Partial<Party>): Party => ({
  name: party?.name || '',
  document: party?.document || '',
  email: party?.email || '',
  phone: party?.phone || '',
  address: ensureAddress(party?.address)
});

const addrLine = (a: Address): string => {
  const parts: string[] = [];
  
  if (a.street) {
    parts.push(a.street);
  }
  
  if (a.city && a.state) {
    const stateAbbr = a.state.substring(0, 2).toUpperCase();
    parts.push(`${a.city} - ${stateAbbr}`);
  }
  
  return parts.join(" - ");
};

const normalizeInvoiceData = (input?: Partial<InvoiceData>): InvoiceData => {
  const meta = {
    number: input?.meta?.number || 'INV-001',
    issueDate: input?.meta?.issueDate || new Date().toISOString(),
    dueDate: input?.meta?.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    currency: input?.meta?.currency || 'BRL'
  };

  const issuer = ensureParty(input?.issuer);
  const customer = ensureParty(input?.customer);
  const items = input?.items || [];
  
  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const tax = 0; // Impostos já inclusos no preço do plano
  const total = subtotal; // Total = subtotal sem adicionar impostos

  const totals = {
    subtotal,
    tax,
    total
  };

  const paymentInfo = {
    method: input?.paymentInfo?.method || 'PIX',
    dueDate: input?.paymentInfo?.dueDate || meta.dueDate,
    instructions: input?.paymentInfo?.instructions || 'Pagamento via PIX'
  };

  return {
    meta,
    issuer,
    customer,
    items,
    totals,
    paymentInfo,
    notes: input?.notes || '',
    payment: input?.payment,
    pixQrCodeBase64: input?.pixQrCodeBase64,
    boletoUrl: input?.boletoUrl,
  };
};

export default function InvoiceGCP({ data, logoUrl }: InvoiceProps) {
  const invoice = normalizeInvoiceData(data);
  const { generatePdfFromElement } = usePdfFromHtml();

  const handleDownloadPdf = React.useCallback(async () => {
    await generatePdfFromElement('invoice-a4', {
      filename: `invoice-${invoice.meta.number || 'gcp'}.pdf`,
      mode: 'fit-a4',
      paddingPx: 0,
      background: '#ffffff',
    });
  }, [generatePdfFromElement, invoice.meta.number]);


  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Botão Download PDF */}
      <div className="no-print mb-3 flex justify-end">
        <Button onClick={handleDownloadPdf} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Baixar PDF
        </Button>
      </div>

      {/* Invoice Container */}
      <div id="invoice-a4" className="a4-page a4-shadow">
        <div className="a4-content">
          {/* GCP-style Header */}
          <div className="bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 p-1 -mt-[45px] -mx-[45px] mb-4">
            <div className="bg-white p-4">
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-8 mb-3 flex-shrink-0" />
                ) : (
                  <div className="text-xl font-bold mb-3 bg-gradient-to-r from-blue-600 via-green-600 to-yellow-600 bg-clip-text text-transparent flex-shrink-0">
                    PortalONE Suite
                  </div>
                )}
                <h1 className="text-2xl font-light text-gray-800 mb-1 whitespace-nowrap">Fatura</h1>
                <div className="flex items-center space-x-4 flex-wrap">
                  <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium break-words">
                    #{invoice.meta.number}
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                    ATIVA
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0 max-w-[250px]">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 shadow-inner">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Data de Emissão</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-800">{fmtDate(invoice.meta.issueDate)}</p>
                    
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Vencimento</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-800">{fmtDate(invoice.meta.dueDate)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Content Area */}
          <div>
          {/* Parties Information */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Issuer Card */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-3 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mr-3"></div>
                  <h3 className="text-lg font-semibold text-gray-800">Emitente</h3>
                </div>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold text-xl text-gray-800">{invoice.issuer.name}</p>
                <div className="flex items-center space-x-2">
                  <span className="bg-gray-100 px-2 py-1 rounded text-xs">CNPJ</span>
                  <span className="font-mono text-sm">{invoice.issuer.document}</span>
                </div>
                <p className="text-sm flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  {invoice.issuer.email}
                </p>
                <p className="text-sm">{invoice.issuer.phone}</p>
                <div className="text-sm text-gray-600 mt-3">{addrLine(invoice.issuer.address)}</div>
              </div>
            </div>

            {/* Customer Card */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-3 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-gradient-to-r from-green-500 to-yellow-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">Cliente</h3>
              </div>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold text-xl text-gray-800">{invoice.customer.name}</p>
                <div className="flex items-center space-x-2">
                  <span className="bg-gray-100 px-2 py-1 rounded text-xs">CNPJ</span>
                  <span className="font-mono text-sm">{invoice.customer.document}</span>
                </div>
                <p className="text-sm flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  {invoice.customer.email}
                </p>
                <p className="text-sm">{invoice.customer.phone}</p>
                <div className="text-sm text-gray-600 mt-3">{addrLine(invoice.customer.address)}</div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-4">
            <div className="flex items-center mb-3">
              <div className="w-3 h-3 bg-gradient-to-r from-yellow-500 to-red-500 rounded-full mr-2"></div>
              <h3 className="text-sm font-semibold text-gray-800">Itens da Fatura</h3>
            </div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <table className="a4-table">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-700 col-desc">Descrição</th>
                    <th className="text-xs font-semibold text-gray-700 col-qtd">Quantidade</th>
                    <th className="text-xs font-semibold text-gray-700 col-unit">Valor Unit.</th>
                    <th className="text-xs font-semibold text-gray-700 col-total">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoice.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="text-gray-800 font-medium text-xs">{item.description}</td>
                      <td className="text-gray-700">
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                          {item.quantity}
                        </span>
                      </td>
                      <td className="font-mono text-gray-700 text-xs">{fmtBRL(item.unitPrice)}</td>
                      <td className="font-semibold font-mono text-gray-800 text-xs">{fmtBRL(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section - Alinhado à direita */}
          <div className="flex justify-end mb-4">
            <div className="w-80">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 shadow-inner">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-mono text-gray-800">{fmtBRL(invoice.totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Impostos:</span>
                    <span className="font-mono text-gray-800">{fmtBRL(invoice.totals.tax)}</span>
                  </div>
                  <div className="border-t-2 border-gray-300 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-semibold text-gray-800">Total:</span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent font-mono">
                        {fmtBRL(invoice.totals.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information - Seção separada */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-blue-500 rounded-full mr-2"></div>
              <h3 className="text-sm font-semibold text-gray-800">Informações de Pagamento</h3>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-3 border border-blue-200">
              <p className="text-sm text-gray-700 bg-white/50 p-3 rounded">
                {invoice.paymentInfo.instructions}
              </p>

              {invoice.payment?.pixPayload && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* QR Code - Maior */}
                  {invoice.pixQrCodeBase64 ? (
                    <div className="flex justify-center">
                      <img 
                        src={invoice.pixQrCodeBase64.startsWith('data:image') ? invoice.pixQrCodeBase64 : `data:image/png;base64,${invoice.pixQrCodeBase64}`}
                        alt="QR Code PIX" 
                        className="border rounded-lg p-2 w-48 h-48 object-contain bg-white"
                      />
                    </div>
                  ) : (
                    <div className="border rounded-lg p-2 h-48 grid place-items-center text-xs text-blue-500">
                      QR PIX não disponível
                    </div>
                  )}
                  
                  {/* PIX Copia e Cola */}
                  <div className="md:col-span-2">
                    <div className="font-medium text-sm mb-2">PIX Copia e Cola:</div>
                    <div className="p-2 bg-white rounded border text-xs font-mono break-all max-h-32 overflow-auto text-gray-900">
                      {invoice.payment.pixPayload}
                    </div>
                    {invoice.payment.pixKey && (
                      <div className="mt-3 text-sm">
                        <span className="font-medium">Chave PIX:</span> {invoice.payment.pixKey}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(invoice.payment?.boletoBarcode || invoice.payment?.boletoLine) && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Informações de Pagamento - Boleto
                  </h4>
                  <BoletoBarcode
                    barcode={invoice.payment.boletoBarcode}
                    digitableLine={invoice.payment.boletoLine}
                    boletoUrl={invoice.boletoUrl}
                  />
                </div>
              )}
            </div>
          </div>

          </div>

          {/* Rodapé com Observações */}
          <footer className="mt-4 pt-2 border-t">
          <div className="text-center text-[10px] text-gray-400 space-y-0.5">
            <p>Esta fatura não substitui a emissão de documento fiscal quando obrigatório (NF-e/NFS-e).</p>
            <p>Multa de 2% e juros de 1% a.m. após o vencimento (se aplicável).</p>
            <p className="mt-1">Fatura gerada automaticamente pelo sistema PortalONE. Para dúvidas entre em contato conosco.</p>
            <p className="mt-1">Fatura gerada automaticamente pelo sistema PortalONE. Para dúvidas entre em contato conosco.</p>
          </div>
          </footer>
        </div>
      </div>
    </div>
  );
}